import {
  DEFAULT_RESTORE_PATH,
  RESET_ROUTE_PATH,
  RETURN_URL_PARAM,
} from './site-data-reset.constants';

// `/latest.html` is an alias for links shared outside the app. Every Angular
// service worker, however old, sends navigations to a URL with a dot to the
// network, so the alias reaches the current code even when an old service
// worker would answer `/latest` from its cache.
const RESET_ROUTE_SEGMENTS = new Set([
  RESET_ROUTE_PATH.slice(1),
  `${RESET_ROUTE_PATH.slice(1)}.html`,
]);

/**
 * True when the Angular router would resolve `pathname` to the reset route,
 * or when it is the `/latest.html` alias. The router matches on decoded segments with matrix params stripped, so a
 * raw string compare against `RESET_ROUTE_PATH` under-matches: `/latest;x=1`
 * and `/%6Catest` both route to `latest` while comparing unequal — enough
 * for a crafted link to re-enter the reset in an endless clear-and-reload
 * loop. Anything deeper under the reset route (`/latest/`, `/latest/x`)
 * counts too, so `return` values pointing there are rejected as well.
 */
export function isResetRoutePath(pathname: string): boolean {
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // Malformed percent-escapes: the router can't parse these either, so
    // they can never reach the reset route.
    return false;
  }
  const firstSegment = decoded.split('/')[1]?.split(';')[0];
  return firstSegment !== undefined && RESET_ROUTE_SEGMENTS.has(firstSegment);
}

/**
 * The slice of `Window` these helpers actually read. `Window` satisfies it
 * structurally, and so does `{ location: new URL(...) }` — which is what the
 * spec passes.
 */
export interface ResetUrlContext {
  location: {
    origin: string;
    pathname: string;
    search: string;
    hash: string;
  };
}

/**
 * Builds the full `/latest` URL, carrying the current location in a `return`
 * query param so the reset can restore the user with a real navigation
 * afterwards. `history.back()` is not an option there: history traversal may
 * be served from the back/forward cache, restoring the stale page from memory
 * with no network fetch.
 */
export function buildResetUrl(win: ResetUrlContext): string {
  const url = new URL(RESET_ROUTE_PATH, win.location.origin);
  if (!isResetRoutePath(win.location.pathname)) {
    url.searchParams.set(
      RETURN_URL_PARAM,
      win.location.pathname + win.location.search + win.location.hash
    );
  }
  return url.toString();
}

/**
 * Resolves where to send the user after the reset. Only same-origin targets
 * are honored — absolute URLs and protocol-relative `//host` values fall back
 * to `DEFAULT_RESTORE_PATH`, as does a missing or unparsable param, or a
 * `return` pointing back at the reset route itself (which would loop).
 */
export function resolveRestoreUrl(win: ResetUrlContext): string {
  const fallback = new URL(
    DEFAULT_RESTORE_PATH,
    win.location.origin
  ).toString();
  const raw = new URLSearchParams(win.location.search).get(RETURN_URL_PARAM);
  if (raw === null) {
    return fallback;
  }

  let target: URL;
  try {
    target = new URL(raw, win.location.origin);
  } catch {
    return fallback;
  }

  if (
    target.origin !== win.location.origin ||
    isResetRoutePath(target.pathname)
  ) {
    return fallback;
  }

  return target.toString();
}
